import React from "react";
import { Text, View, StyleSheet } from "@react-pdf/renderer";
import { withNamespaces } from "react-i18next";
import { formatTaskAndRiskId } from "src/modules/data-protection/constants";

const styles = StyleSheet.create({
    section: {
        margin: 10,
        padding: 20,
        paddingTop: 0,
        paddingBottom: 0
    },
    sectionTitle: {
        fontWeight: 800,
        fontSize: 13,
        marginBottom: 12
    },
    sectionBody: {
        margin: 0,
        padding:0
    },
    pText: {
        fontWeight: 400,
        marginBottom: 12,
        fontSize: 11,
    },
    fontBold: {
        fontWeight: 'bold'
    },
    table: {
        display: "table",
        width: "auto",
        marginBottom: '10px',
        borderStyle: "solid",
        borderWidth: 1,
        borderColor: "#000",
    },
    tableRow: {
        flexDirection: "row",
        borderBottom: "1px solid #000"
    },
    tableLastRow : {
        borderBottom: 'none'
    },
    tableColHeader: {
        width: "50%",
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "flex-start",
        backgroundColor: "#eeeeee",
        borderRight: '1px solid #000'
    },
    tableCol: {
        width: "50%",
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "flex-start",
        borderRight: '1px solid #000',
        paddingTop: 2,
        paddingBottom: 2
    },
    tableCell: {
        margin: 5,
        fontSize: 8,
        maxWidth: "100%", 
        wordWrap: "break-word",
        whiteSpace: "normal",
        overflow: "hidden"
    },
    coordsCell: {
        margin: 5,
        fontSize: 8,
        backgroundColor: 'red'
    },
    borderRightNone: {
        borderRight: 'none'
    },
    list: {
        marginVertical: 4,
        marginLeft: 12,
    },
    listItem: {
        flexDirection: "row",
        marginBottom: 4,
    },
    bullet: {
        width: 10,
        fontSize: 12,
    },
    listText: {
        flex: 1,
        fontWeight: 400,
        fontSize: 11,
    },
});

const TableHeader = ({ t }) => {
    return (
        <View style={styles.tableRow}>
            <View style={styles.tableColHeader}>
                <Text style={{
                    ...styles.tableCell,
                    ...styles.fontBold
                }}>ID</Text>
            </View>

            <View style={styles.tableColHeader}>
                <Text style={{
                    ...styles.tableCell,
                    ...styles.fontBold
                }}>{t('Controls according to ISO/IEC 27001')}</Text>
            </View>

            <View style={styles.tableColHeader}>
                <Text style={{
                    ...styles.tableCell,
                    ...styles.fontBold
                }}>
                    {t('Applicable(YES/NO)')}
                </Text>
            </View>

            <View style={styles.tableColHeader}>
                <Text style={{
                    ...styles.tableCell,
                    ...styles.fontBold
                }}>{t('Justification for selection/ exclusion')}</Text>
            </View>

            <View style={styles.tableColHeader}>
                <Text style={{
                    ...styles.tableCell,
                    ...styles.fontBold
                }}>{t('Implementation method')}</Text>
            </View>

            <View style={{
                ...styles.tableColHeader,
                ...styles.borderRightNone
            }}>
                <Text style={{
                    ...styles.tableCell,
                    ...styles.fontBold
                }}>{t('Status')}</Text>
            </View>
        </View>
    )
}

const TableRow = ({ t, control, key }) => {
    const rowStyle = {
        ...styles.tableRow,
        ...(((key + 1) >= 14) && { ...styles.tableLastRow })
    }

    return (
        <View style={rowStyle}>
            <View style={styles.tableCol}>
                <Text style={styles.tableCell}>
                    {`A${control.controlNumber}`}
                </Text>
            </View>

            <View style={styles.tableCol}>
                <Text style={styles.tableCell}>
                    {t(control.title)}
                </Text>
            </View>

            <View style={styles.tableCol}>
                <Text style={styles.tableCell}>
                    {t(control.applicable ? 'Yes' : 'No')}
                </Text>
            </View>

            <View style={styles.tableCol}>
                <Text style={styles.tableCell}>
                    <Text>
                        {control.notApplicableReason || ""}
                    </Text>

                    {control.notApplicableReason && control.risks?.length > 0 && (
                        <Text>{', '}</Text>
                    )}
                    
                    <Text>
                        {(control.risks || []).map(({
                            asset_id,
                            case_secondary_id,
                            secondary_id,
                            is_mitigation,
                            questionnaire
                        }, pIndex) => {
                            return (
                                <Text key={pIndex}>
                                    {formatTaskAndRiskId(
                                        'risk', 
                                        questionnaire === "software" ? asset_id : case_secondary_id, 
                                        secondary_id, 
                                        is_mitigation, 
                                        questionnaire, 
                                        null
                                    )}

                                    {pIndex < (control.risks?.length - 1) ? ', ' : ''}
                                </Text>
                            )
                        })}
                    </Text>
                </Text>
            </View>

            <View style={styles.tableCol}>
                <Text style={styles.tableCell}>
                    <Text>
                        {(control.documents || []).map((document, pIndex) => {
                            return (
                                <Text key={pIndex}>
                                    {document.title}
                                    {pIndex < (control.documents?.length - 1) ? ', ' : ''}
                                </Text>
                            )
                        })}
                    </Text>
                </Text>
            </View>

            <View style={{
                ...styles.tableCol,
                ...styles.borderRightNone
            }}>
                <Text style={styles.tableCell}>
                    {t(control.applicable ? 'Implemented' : '')}
                </Text>
            </View>
        </View>
    )
}

const PageContent = (props) => {
    const { controls, organization, t } = props;

    const rowsPerPage = 14;
    const pages = [];

    for (let i = 0; i < controls.length; i += rowsPerPage) {
        pages.push(controls.slice(i, i + rowsPerPage));
    }

    return (
        <>
            <View style={styles.section}>
                <Text style={styles.sectionTitle}>
                    {`1. ${t('Purpose, scope and users')}`}
                </Text>

                <View style={styles.sectionBody}>
                    <Text style={styles.pText}>
                        {t('The purpose of this document is to define which security controls are to be implemented in {{company}}.', {
                            'company': organization.name
                        })}
                    </Text>

                    <Text style={styles.pText}>
                        {t('This document includes all controls listed in Annex A of the ISO 27001 standard. Controls are applicable to the entire Information Security Management System (ISMS) scope.')}
                    </Text>

                    <Text style={styles.pText}>
                        {t('Users of this document are all employees of {{company}} who have a role in the ISMS.', {
                            'company': organization.name
                        })}
                    </Text>
                </View>
            </View>

            <View style={styles.section}>
                <Text style={styles.sectionTitle}>
                    {`2. ${t('Reference documents')}`}
                </Text>

                <View style={styles.sectionBody}>
                    <View style={styles.list}>
                        <View style={styles.listItem}>
                            <Text style={styles.bullet}>•</Text>
                            <Text style={styles.listText}>
                                {t('ISO/IEC 27001 standard, clause 6.1.3 d)')}
                            </Text>
                        </View>

                        <View style={styles.listItem}>
                            <Text style={styles.bullet}>•</Text>
                            <Text style={styles.listText}>
                                {t('Information Security Policy')}
                            </Text>
                        </View>

                        <View style={styles.listItem}>
                            <Text style={styles.bullet}>•</Text>
                            <Text style={styles.listText}>
                                {t('Risk Assessment and Risk Treatment Methodology')}
                            </Text>
                        </View>

                        <View style={styles.listItem}>
                            <Text style={styles.bullet}>•</Text>
                            <Text style={styles.listText}>
                                {t('Risk Assessment and Risk Treatment Report')}
                            </Text>
                        </View>
                    </View>
                </View>
            </View>

            <View style={styles.section}>
                <Text style={styles.sectionTitle}>
                    {`3. ${t('Applicability of controls')}`}
                </Text>

                <View style={styles.sectionBody}>
                    <Text style={styles.pText}>
                        {t('The following controls from ISO 27001 Annex A are applicable:')}
                    </Text>
                    <View style={{ display: "flex" }}>
                        {pages.map((pageRows, pageIndex) => {
                            return (
                                <View style={styles.table} key={pageIndex} wrap={true} break>
                                    <TableHeader t={t} />
                                    {pageRows.map((control, cIndex) => (
                                        <TableRow
                                            key={cIndex}
                                            control={control}
                                            t={t}
                                        />
                                    ))}
                                </View>
                            )
                        })}
                    </View>
                </View>
            </View>
        </>
    );
};

export default withNamespaces()(PageContent);
